import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from '@apollo/react-hoc';
import { FormattedMessage, defineMessages } from 'react-intl';
import Icon from '../../components/Icon';
import Button from '../../components/Button';
import Header from '../../components/Header';
import Modal from '../../components/Modal';
import Loader from '../../components/Loader';
import Dimmer from '../../components/Dimmer';
import Message from '../../components/Message';
import updateHSUserMutation from '../../mutations/HSAccount/updateHSUser';
import { SimpleGraphQLComponent } from '../../utils/apollo';

const messages = defineMessages({
  error: {
    id: 'primary-user.re-invite-user.error',
    defaultMessage: 'Unable to re-invite user',
  },
  reInviteUser: {
    id: 'primary-user.re-invite-user.header',
    defaultMessage: 'Re-invite User',
  },
  reInviteUserText: {
    id: 'primary-user.re-invite-user.text',
    defaultMessage: 'Would you like to re-invite {userName}?',
  },
});

export class HSAccountReInviteUserComponent extends React.PureComponent {
  static propTypes = {
    hsAccount: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    user: PropTypes.shape({
      id: PropTypes.string.isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
    }).isRequired,
    mutateUpdateHSUser: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
  };

  state = {
    isError: false,
    requesting: false,
  };

  _handleClose = () => {
    this.setState({ requesting: false });
    this.props.onClose();
  };

  _handleSave = () => {
    const { user, hsAccount } = this.props;

    this.setState({ isError: false, requesting: true });
    this.props.mutateUpdateHSUser({
      variables: {
        input: {
          hsAccount: {
            id: hsAccount.id,
            user: {
              id: user.id,
              recreate: true,
            },
          },
        },
      },
    }).then(this._handleClose, () => {
      this.setState({ isError: true, requesting: false });
    });
  };

  render() {
    const { user } = this.props;
    const { isError, requesting } = this.state;
    const userName = `${user.firstName} ${user.lastName}`;
    const errorDetails = isError ? (
      <Message role="alert" negative>
        <Message.Header><FormattedMessage {...messages.error} /></Message.Header>
      </Message>
    ) : '';

    return (
      <Dimmer.Dimmable as={Modal} open onClose={this._handleClose} data-cy="hs-reInvite-user-close-button">
        <Dimmer
          tabIndex={-1}
          role="progressbar"
          aria-describedby="Re-inviting User..."
          aria-busy={requesting}
          inverted
          active={requesting}
        >
          <Loader active={requesting} />
        </Dimmer>
        <Header data-cy="hs-reInvite-user-header-title">
          <FormattedMessage {...messages.reInviteUser} />
        </Header>
        <Modal.Content data-cy="hs-reInvite-user-content-text">
          <p>
            <FormattedMessage
              {...messages.reInviteUserText}
              values={{
                userName,
              }}
            />
          </p>
          {errorDetails}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={this._handleClose} data-cy="hs-reInvite-user-no-button">
            <Icon name="remove" />
            <FormattedMessage id="common.button.no" defaultMessage="No" />
          </Button>
          <Button primary onClick={this._handleSave} data-cy="hs-reInvite-user-yes-button">
            <Icon name="checkmark" />
            <FormattedMessage id="common.button.yes" defaultMessage="Yes" />
          </Button>
        </Modal.Actions>
      </Dimmer.Dimmable>
    );
  }
}

const updateHSUser = graphql(updateHSUserMutation, {
  name: 'mutateUpdateHSUser',
});

export default SimpleGraphQLComponent(updateHSUser)(HSAccountReInviteUserComponent);
